import type { LinkProps } from 'next/link';
import type { ComponentPropsWithoutRef, Ref } from 'react';

import NextLink from 'next/link';

import { resolveVsLink, type VsLinkType } from '@vs-shop/headless/vsLink';

// All possible props: Next.js Link props + HTML anchor props + our link type.
// `href` is narrowed to a required string — every call site passes a string, so
// the plain <a> branch never needs URL formatting.
//
// Superset of the portable `VsLinkProps` render contract (`@vs-shop/headless/vsLink`)
// — any `VsLinkProps` value is a valid `VsLinkComponentProps` value here. See
// `VsLink.tst.ts` for the compile-time proof.
type VsLinkComponentProps = Omit<LinkProps, 'ref' | 'href'> &
  Omit<ComponentPropsWithoutRef<'a'>, keyof LinkProps> & {
    type?: VsLinkType;
    href: string;
    ref?: Ref<HTMLAnchorElement>;
  };

export default function VsLink({ ref, ...props }: VsLinkComponentProps) {
  const resolved = resolveVsLink(props);
  // Attach `ref` only when defined — Base UI's `render={<VsLink/>}` injects one,
  // but next/link's props reject an explicit `undefined` under
  // exactOptionalPropertyTypes.
  const refProp = ref === undefined ? {} : { ref };

  if (resolved.useNextLink) {
    return <NextLink {...resolved.nextLinkProps} {...refProp} />;
  }

  return <a {...resolved.anchorProps} href={resolved.href} {...refProp} />;
}
